'use strict';

const {
    HIDDEN_CLASS,
    ACTIVE_CLASS,
    EVENT_KEY_ENTER,
    EVENT_KEY_SPACE,
    EVENT_KEY_TAB,
    EVENT_KEY_UP,
    EVENT_KEY_DOWN,
    EVENT_KEY_HOME,
    EVENT_KEY_END,
    EVENT_KEY_ESC
} = require('../constants');

const { queryFirst, queryAll, removeClass, addClass, hasClass } = require('../domUtil');

const $body = $('body');
let lastInteractedEl = null;

// modals that open other modals and remain on page after child modals are closed:
const TOP_OVERLAYS = ['#minicart', '#stylitics-custom-quickview'];

/* ---------- Helpers used only by init ---------- */
function getTopOverlay$() {
    // clear priority:
    for (const sel of TOP_OVERLAYS) {
        const $el = $(`${sel}.show`);
        if ($el.length) return $el;
    }
    // fallback: any other open modal:
    const $fallback = $('.modal.show, .sheet.show');
    return $fallback.length ? $fallback.last() : $();
}

function getFocusable($root, opts = {}) {
    const $r = $root && $root.jquery ? $root : $($root);
    if (!$r.length) {
        return $();
    }

    let $scope = $r;
    const rootEl = $r[0];
    if (opts.sheetPrimaryRegion && rootEl.nodeType === 1 && hasClass(rootEl, 'sheet')) {
        const page = queryFirst('.sheet-page.active', rootEl) || queryFirst('.sheet-page', rootEl);
        if (page) {
            const $content = $(page).find('.sheet-body, .sheet-header, .sheet-footer');
            $scope = $content.length ? $content : $scope;
        }
    }

    return $scope
        .find(`
            input:not([disabled]):not([type="hidden"]),
            select:not([disabled]),
            textarea:not([disabled]),
            button:not([disabled]),
            a[href],
            [tabindex]:not([tabindex="-1"]),
            .sheet-body-content
        `).filter(function () {
            if (!this.offsetParent) return false;

            const sheetPage = this.closest('.sheet-page');
            if (sheetPage) {
                const sheet = sheetPage.closest('.sheet');
                const pages = sheet ? sheet.querySelectorAll('.sheet-page') : [];
                if (pages.length > 1) {
                    const activePage = sheet.querySelector('.sheet-page.active');
                    let visiblePage = activePage || pages[0];

                    // ISPU: keep tab order on the results page while store list is still shown
                    if (sheet.id && sheet.id.includes('pick-up-in-store')) {
                        const contentPage = sheet.querySelector('.sheet-page.pickup-in-store-content');
                        const storeResults = contentPage?.querySelector('.pick-up-in-stores');
                        if (storeResults?.innerHTML.trim()) {
                            visiblePage = contentPage;
                        }
                    }

                    if (sheetPage !== visiblePage) return false;
                }
            }

            if (hasClass(this, 'sheet-body-content')) {
                return this.scrollHeight > this.clientHeight;
            }
            return true;
        });
}

function focusPreferNative(el) {
    if (!el) return;
    try {
        el.focus({ preventScroll: true });
    } catch {
        el.focus();
    }
}

function trapFocus($modal, opts = {}) {
    const ns = '.trapFocus';

    function onKeydown(e) {
        if (e.key !== EVENT_KEY_TAB) return;

        const $f = getFocusable($modal);
        if (!$f.length) return;

        if ($f.length === 1) {
            e.preventDefault();
            $f[0].focus();
            return;
        }

        const first = $f[0];
        const last = $f[$f.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }

    if (!$modal.data('trapHandler')) {
        $modal.on('keydown' + ns, onKeydown);
        $modal.data('trapHandler', onKeydown);
    }

    if (opts.suppressInitial) {
        return;
    }

    requestAnimationFrame(() => {
        const preferred = opts.initialFocus;
        if (preferred && $modal[0].contains(preferred)) {
            focusPreferNative(preferred);
            return;
        }

        const $tabsActive = $modal.find('.js-tab-controls button.' + ACTIVE_CLASS).first();
        const $regionFocusables = getFocusable($modal, { sheetPrimaryRegion: true });
        const $formField = $regionFocusables.filter('input:not([type="hidden"]), textarea, select').first();
        const $initial = $tabsActive.length
            ? $tabsActive
            : ($formField.length ? $formField : $regionFocusables.first());

        if ($initial.length) {
            focusPreferNative($initial[0]);
            return;
        }

        const hadTabindex = $modal.attr('tabindex');

        $modal.attr('tabindex', '-1').trigger('focus');

        if (hadTabindex === undefined) {
            $modal.removeAttr('tabindex');
        } else {
            $modal.attr('tabindex', hadTabindex);
        }
    });
}

function getFocusTarget(opener) {
    if (!opener) return null;

    // Normalize to DOM element
    let el = opener;

    if (opener.jquery || (opener[0] && typeof opener[0].focus === 'function')) {
        el = opener[0];
    }

    if (!el) return null;

    // If opener specifies alternative focus target
    try {
        if (el.getAttribute) {
            const { modalOpener: selector } = el.dataset;

            if (selector) {
                const alt = document.querySelector(selector);

                if (alt) {
                    el = alt;
                }
            }
        }
    } catch (_) {
        // Bad selector, ignore and fallback to original element
    }

    return el;
}

function focusBack(opener) {
    const target = getFocusTarget(opener);
    if (!target) return;

    try {
        if (typeof target.focus === 'function') {
            // Optional: skip hidden elements
            if (!(target.offsetParent === null)) {
                target.focus();
            } else {
                target.focus(); // or just focus anyway if you don't care about visibility
            }
        }
    } catch (_) {}
}

function releaseFocus(el, opener) {
    if (!el) {
        focusBack(opener);
        return;
    }

    const handler = el.data('trapHandler');

    if (handler) {
        el.off('keydown.trapFocus', handler);
    }

    el.removeData('trapHandler');

    focusBack(opener);
}

/* ---------- Export: single function for processInclude ---------- */

module.exports = function () {
    // Remember last interacted element via keyboard
    document.addEventListener(
        'keydown',
        (e) => {
            if (!e.isTrusted) return;

            if (e.key === EVENT_KEY_ENTER || e.key === EVENT_KEY_SPACE) {
                lastInteractedEl = e.target;
            }
        },
        true
    );

    // Remember last interacted element via pointer (ignore minicart non-user triggers)
    const onPointerLikeDown = (e) => {
        if (!e.isTrusted) return;

        const isMinicart =
            !!(e.target && (e.target.closest('.minicart-link') || e.target.closest('[data-target="#minicart"]')));

        if (isMinicart) return;

        lastInteractedEl = e.target;
    };

    if ('onpointerdown' in document) {
        document.addEventListener('pointerdown', onPointerLikeDown, true);
    } else {
        document.addEventListener('mousedown', onPointerLikeDown, true);
    }

    // Close the current modal
    $body.on('click',
        `.close-current-modal,
        .js-close-sheet > .modal-back,
        .js-close-on-done .modal-back`, function (event) {
        const currentModal = $(event.target).closest('.lp-slideout-modal, .sheet');

        if (currentModal.length) {
            currentModal.modal('hide');
        };
    });

    // Hidden: keep body state, cleanup backdrops, release focus
    $body.on('hidden.bs.modal', (e) => {
        const $modal = $(e.target);
        const opener = $modal.data('opener');

        if (queryFirst('.modal.show')) {
            $body.addClass('modal-open');
        } else {
            queryAll('.modal-backdrop').forEach((el) => el.remove());
            $body.removeClass('modal-open');
        }

        const handler = $modal.data('trapHandler');
        if (handler) {
            $modal.off('keydown.trapFocus', handler);
            $modal.removeData('trapHandler');
        }

        const $top = getTopOverlay$();

        if ($top.length) {
            requestAnimationFrame(() => {
                // return focus to opener in the modal:
                try { opener && opener.focus({ preventScroll: true }); } catch { opener && opener.focus && opener.focus(); }

                // trap focus inside the modal without moving focus:
                trapFocus($top, { suppressInitial: true });
            });
        } else {
            // no other modals open:
            releaseFocus($modal, opener);
        }
    });

    // Shown: separators, custom scrollbars, focus, swiper reset
    $(document).on('shown.bs.modal', function (e)  {
        const { resetSwiperInModal } = require('./modalApi');
        const $modal = $(e.target);

        $body.addClass('modal-open');
        $modal.data('opener', lastInteractedEl);

        trapFocus($modal);
        resetSwiperInModal(e.target);

        const sheet = queryFirst('.sheet.show');
        const modal = queryFirst('.modal.show');

        if (sheet) {
            const OBSERVING_CLASS = 'observing';
            const pages = queryAll('.sheet-page', sheet);

            pages.forEach((page) => {
                const sheetBody = queryFirst('.sheet-body-content', page);
                const separator = queryFirst('.sheet-body-separator', page);
                const sheetBodyEnd = sheetBody && queryFirst('.sheet-body-end', sheetBody);

                if (sheetBody && separator) {
                    const contentHeight = [].slice.call(sheetBody.children).reduce((t, c) => t + c.offsetHeight, 0);

                    if (sheetBody.offsetHeight < contentHeight) {
                        removeClass(separator, HIDDEN_CLASS);
                    }

                    if (sheetBodyEnd && !hasClass(sheetBodyEnd, OBSERVING_CLASS)) {
                        const bodyEndObserver = new IntersectionObserver((entries) => {
                            entries.forEach((entry) => {
                                if (entry.isIntersecting && !hasClass(separator, HIDDEN_CLASS)) {
                                    addClass(separator, HIDDEN_CLASS);
                                } else if (!entry.isIntersecting && hasClass(separator, HIDDEN_CLASS)) {
                                    removeClass(separator, HIDDEN_CLASS);
                                }
                            });
                        });

                        addClass(sheetBodyEnd, OBSERVING_CLASS);
                        bodyEndObserver.observe(sheetBodyEnd);
                    }
                }
            });

            const modalContents = queryAll('.sheet-body-content', sheet);
            const scrollbarThumbs = queryAll('.custom-scrollbar-thumb', sheet);

            modalContents.forEach((modalContent, i) => {
                const scrollbarThumb = scrollbarThumbs[i];

                if (!scrollbarThumb) return;

                const updateScrollbar = () => {
                    const { scrollHeight: contentHeight, clientHeight: visibleHeight, scrollTop } = modalContent;
                    const scrollHeight = contentHeight - visibleHeight;

                    const thumbH = Math.max((visibleHeight / contentHeight) * 100, 10);
                    scrollbarThumb.style.height = thumbH === 100 ? 0 : `${thumbH}%`;

                    const thumbPos = (scrollTop / scrollHeight) * (100 - thumbH);
                    scrollbarThumb.style.top = `${thumbPos}%`;
                };

                modalContent.addEventListener('scroll', updateScrollbar);
                window.addEventListener('resize', updateScrollbar);
                updateScrollbar();
            });
        } else if (modal) {
            const pages = queryAll('.modal-content', modal);

            pages.forEach((page) => {
                const modalBody = queryFirst('.modal-body', page);
                const modalFooter = queryFirst('.modal-footer', page);
                const modalBodyEnd = queryFirst('.modal-body-end', page);

                const SEPARATOR_CLASS = 'separator';
                const OBSERVING_CLASS = 'observing';

                if (modalBody && modalFooter && modalBodyEnd) {
                    const modalH = [].slice.call(modalBody.children).reduce((t, c) => t + c.offsetHeight, 0);

                    if (modalBody.offsetHeight < modalH) {
                        addClass(modalFooter, SEPARATOR_CLASS);
                    } else {
                        removeClass(modalFooter, SEPARATOR_CLASS);
                    }

                    if (!hasClass(modalBodyEnd, OBSERVING_CLASS)) {
                        const bodyEndObserver = new IntersectionObserver((entries) => {
                            entries.forEach((entry) => {
                                if (!entry.isIntersecting && !hasClass(modalFooter, SEPARATOR_CLASS)) {
                                    addClass(modalFooter, SEPARATOR_CLASS);
                                } else if (entry.isIntersecting && hasClass(modalFooter, SEPARATOR_CLASS)) {
                                    removeClass(modalFooter, SEPARATOR_CLASS);
                                }
                            });
                        });

                        addClass(modalBodyEnd, OBSERVING_CLASS);
                        bodyEndObserver.observe(modalBodyEnd);
                    }
                }

                if (modalBody) {
                    modalBody.scrollTo(0, 0);
                }
            });
        }
    });

    // ESC to close
    document.addEventListener('keydown', function (e) {
        if (e.key !== EVENT_KEY_ESC) return;
        e.preventDefault();
        e.stopImmediatePropagation();

        const $top = getTopOverlay$();
        if (!$top.length) return;

        const $btn = $top.find('.close-current-modal').first();
        if ($btn.length) {
            $btn.trigger('click');
        } else {
            $top.modal('hide');
        }
    });

    // Reset scrollTop on hide
    $body.on('hide.bs.modal', (e) => {
        const { AlertDialog } = require('./alertDialog');

        AlertDialog.closeAll(null, { instant: true });
        queryAll('.sheet-body-content', e.currentTarget)?.forEach((sheetBody) => {
            sheetBody.scrollTop = 0;
        });
    });

    $body.on('show.bs.modal', (e) => {
        const { AlertDialog } = require('./alertDialog');

        AlertDialog.closeAll(null, { instant: true });
    });

    // Ajax-loaded tabbed content
    $body.on('modalContentLoaded', (e) => {
        const { resetSwiperInModal } = require('./modalApi');

        const $from = $(e.target);
        const $container = $from.closest('.modal, .sheet');

        if ($container.length) {
            trapFocus($container);
            resetSwiperInModal($container[0]);
        }
    });

    // Tabs: click handler delegates to modalApi.activateTab
    $body.on('click', '.modal .js-tab-controls button', (e) => {
        const { activateTab } = require('./modalApi');
        activateTab($(e.currentTarget));
    });

    // Tabs: keyboard navigation
    $body.on('keydown', '.modal.show .js-tab-controls button', (e) => {
        const $controls = $(e.currentTarget).closest('.js-tab-controls');
        const $tabs = $controls.find('button');

        const currentIndex = $tabs.index(e.currentTarget);

        if (currentIndex === -1) return;

        let newIndex = currentIndex;

        switch (e.key) {
            case EVENT_KEY_DOWN:
                e.preventDefault();
                newIndex = (currentIndex + 1) % $tabs.length;
                break;

            case EVENT_KEY_UP:
                e.preventDefault();
                newIndex = (currentIndex - 1 + $tabs.length) % $tabs.length;
                break;

            case EVENT_KEY_HOME:
                e.preventDefault();
                newIndex = 0;
                break;

            case EVENT_KEY_END:
                e.preventDefault();
                newIndex = Math.max(0, $tabs.length - 1);
                break;

            case EVENT_KEY_ENTER:
            case EVENT_KEY_SPACE:
                e.preventDefault();
                if (!$tabs.eq(currentIndex).hasClass(ACTIVE_CLASS)) {
                    $tabs.eq(currentIndex).trigger('click');
                }
                return;

            default:
                return;
        }

        $tabs.eq(newIndex).focus();
    });

    // Sheet pages: open target page by data-page class
    $body.on('click', '.sheet-dialog.multipage button.modal-page', (e) => {
        const { scrollSheetTop } = require('./modalApi');

        const { currentTarget } = e;

        const sheet = currentTarget.closest('.sheet');
        if (!sheet) return;

        const pageClass = currentTarget.dataset.page;
        if (!pageClass) return;

        const pages = queryAll('.sheet-page', sheet);
        const target = queryFirst(`.${pageClass}`, sheet);
        if (!target) return;

        removeClass(pages, ACTIVE_CLASS);
        addClass(target, ACTIVE_CLASS);

        requestAnimationFrame(() => scrollSheetTop($(sheet), target));

        // Let existing hooks re-init focus/swiper/etc if needed
        target.dispatchEvent(new CustomEvent('modalContentLoaded', { bubbles: true }));
    });

    // Sheet pages: back -> first page (or “home” page)
    $body.on('click', '.sheet-dialog.multipage button.modal-back', (e) => {
        const { scrollSheetTop } = require('./modalApi');

        const { currentTarget } = e;

        const sheet = currentTarget.closest('.sheet');
        if (!sheet) return;

        const pages = queryAll('.sheet-page', sheet);
        const target = pages[0];
        if (!target) return;

        removeClass(pages, ACTIVE_CLASS);

        requestAnimationFrame(() => scrollSheetTop($(sheet), target));

        target.dispatchEvent(new CustomEvent('modalContentLoaded', { bubbles: true }));

         // --- focus restore ---
        if (currentPage) {
            const pageClass = [...currentPage.classList]
                .find(cls => queryFirst(`.modal-page[data-page="${cls}"]`, sheet));

            if (pageClass) {
                const triggerBtn = queryFirst(`.modal-page[data-page="${pageClass}"]`, sheet);
                triggerBtn?.focus();
            }
        }
    });
};

// Reusable focus-management helpers for custom overlays (e.g. the sticky GWP drawer).
module.exports.trapFocus = trapFocus;
module.exports.releaseFocus = releaseFocus;
